<script setup lang="ts">
// Instagram das unidades (plugin instagram-analytics, plan M6): every post of every unit in one table, narrowed by
// the table's column filters (account, unit, type, direction…). Only @kernel/* imports (kernel-plugin contract,
// surface `frontend`). Text goes through the global `$t` (a plugin does not import vue-i18n).
import { computed, getCurrentInstance, onMounted } from 'vue'
import DataTable from '@kernel/grid/DataTable.vue'
import PageHeader from '@kernel/ui/PageHeader.vue'
import StatsCard from '@kernel/ui/StatsCard.vue'
import InlineMessage from '@kernel/ui/InlineMessage.vue'
import { countText, partnerExplanation, useInstagramAnalytics } from './useInstagramAnalytics'
import type { PostRow } from './useInstagramAnalytics'

const vm = getCurrentInstance()
const t = (key: string): string => (vm?.proxy as unknown as { $t?: (k: string) => string } | undefined)?.$t?.(key) ?? key

const { posts, partnerProfiles, loading, problem, totals, load, thumbnailOf } = useInstagramAnalytics({ published: t('Publicado pela unidade'), invited: t('Convidada') })

const problemText = computed(() => {
  switch (problem.value) {
    case 'not_configured': return t('O acervo de dados não está configurado neste ambiente (VITE_WAREHOUSE_URL).')
    case 'missing_scope': return t('Seu usuário precisa do acesso "Instagram das unidades" em Acervo de dados (Usuários › Permissões).')
    case 'no_access': return t('Seu usuário não tem acesso ao acervo de dados (grupo do acervo).')
    case 'error': return t('Não foi possível carregar o Instagram das unidades. Tente atualizar.')
    default: return ''
  }
})

const columns = computed(() => [
  {
    data: 'shortCode', title: t('Prévia'), renderType: 'image', sortable: false, filterable: false,
    imageConfig: { width: 56, height: 56, preview: true, emptyIcon: 'pi pi-instagram', alt: (row: PostRow) => row.caption.slice(0, 80), src: (row: PostRow) => thumbnailOf(row.shortCode) },
    explanation: { what: t('A capa do post, guardada no acervo na coleta. Aparece o ícone enquanto a capa do post ainda não foi baixada.') },
  },
  { data: 'url', title: t('Post'), renderType: 'clickable_button', buttonText: t('Abrir'), buttonIcon: 'pi pi-external-link', buttonAction: 'open', buttonTestId: (row: PostRow) => `instagram-post-open-${row.shortCode}`, sortable: false },
  { data: 'profile', title: t('Conta') },
  { data: 'units', title: t('Unidades') },
  { data: 'postedAt', title: t('Data'), renderType: 'datetime' },
  { data: 'kind', title: t('Tipo') },
  { data: 'direction', title: t('Collab'), calculated: true, explanation: { what: t('Quem publicou: a própria unidade, ou outro perfil que convidou a unidade como coautora.') } },
  {
    data: 'partners', title: t('Parceiros'), calculated: true,
    explanation: { what: t('Com quem o post foi feito: o dono do post quando a unidade foi convidada, ou os perfis que a unidade convidou. Passe o mouse no parceiro para ver o perfil.') },
    cellExplanation: (row: PostRow) => partnerExplanation(row.partnerHandles, partnerProfiles.value, t),
  },
  {
    data: 'partnerFollowersText', sortField: 'partnerFollowers', title: t('Seguidores dos parceiros'), calculated: true,
    explanation: {
      what: t('Quantas pessoas seguem os perfis com quem o post foi feito, somadas. Passe o mouse nos parceiros para ver cada um.'),
      calculation: [t('Seguidores de cada parceiro na última leitura do perfil'), t('+ dos demais parceiros do post')],
      scope: t('Parceiro cujo perfil ainda não foi lido fica fora da soma; "—" quando nenhum foi lido.'),
    },
    cellExplanation: (row: PostRow) => partnerExplanation(row.partnerHandles, partnerProfiles.value, t),
  },
  { data: 'viewsText', sortField: 'views', title: t('Visualizações'), calculated: true, explanation: { what: t('Reproduções no Instagram na última coleta (sem as do Facebook); "—" em foto, que não tem visualização.') } },
  { data: 'growthText', sortField: 'viewsGrowth', title: t('Crescimento'), calculated: true, explanation: { what: t('Visualizações ganhas desde a primeira coleta deste post; "—" sem visualização.'), calculation: [t('Visualizações na última coleta'), t('− visualizações na primeira coleta')] } },
  { data: 'viewsAt7dText', sortField: 'viewsAt7d', title: t('Visualizações em 7 dias'), calculated: true, explanation: { what: t('Visualizações uma semana depois da publicação: a leitura mais próxima do 7º dia (entre o 5º e o 10º).'), scope: t('"—" quando o post não foi lido nessa janela (posts antigos, carregados depois).') } },
  { data: 'viewsAt30dText', sortField: 'viewsAt30d', title: t('Visualizações em 30 dias'), calculated: true, explanation: { what: t('Visualizações um mês depois da publicação: a leitura mais próxima do 30º dia (entre o 25º e o 37º).'), scope: t('"—" quando o post não foi lido nessa janela.') } },
  { data: 'viewsLastWeekText', sortField: 'viewsLastWeek', title: t('Visualizações na última semana'), calculated: true, explanation: { what: t('Quanto o post cresceu na última semana.'), calculation: [t('Visualizações na última leitura'), t('− visualizações na leitura de pelo menos 7 dias antes')], scope: t('"—" até o post ter duas leituras com uma semana entre elas.') } },
  { data: 'likesText', sortField: 'likes', title: t('Curtidas'), calculated: true, explanation: { what: t('Curtidas na última coleta; "—" quando o perfil oculta as curtidas.') } },
  { data: 'commentsText', sortField: 'comments', title: t('Comentários'), calculated: true, explanation: { what: t('Comentários na última coleta.') } },
  { data: 'caption', title: t('Legenda') },
])

const tableConfig = computed(() => ({ tableId: 'instagramPostsTable', columns: columns.value, pagination: true, pageLength: 25 }))

const filterConfig = computed(() => ({
  searchName: t('Procurar'),
  choosePlaceholder: t('Escolha'),
  refreshName: t('Atualizar'),
  printName: t('Imprimir'),
  columns: {
    profile: 'select',
    units: 'select',
    postedAt: 'date',
    kind: 'select',
    direction: 'select',
    partners: 'text',
    caption: 'text',
  },
}))

/** The post opens on Instagram, in a new tab, with no opener and no referrer. */
function onButton(payload: { data: PostRow | null; action: unknown }): void {
  if (payload.action === 'open' && payload.data?.url) window.open(payload.data.url, '_blank', 'noopener,noreferrer')
}

onMounted(load)
</script>

<template>
  <section class="instagram-analytics-page" data-elem-id="instagram-analytics-page">
    <PageHeader :title="t('Instagram das unidades')" :subtitle="t('Posts, reels e collabs públicos de cada unidade, coletados uma vez por dia.')" icon="pi pi-instagram" />

    <InlineMessage v-if="problem" severity="warn" data-elem-id="instagram-analytics-problem">{{ problemText }}</InlineMessage>

    <template v-else>
      <div class="instagram-analytics-stats">
        <StatsCard :value="countText(totals.profiles)" :label="t('Contas')" icon="pi pi-users" :explanation="{ what: t('Contas de unidade com posts coletados.') }" />
        <StatsCard :value="countText(totals.videos)" :label="t('Vídeos')" icon="pi pi-video" :explanation="{ what: t('Reels e vídeos coletados de todas as unidades.') }" />
        <StatsCard :value="countText(totals.collabs)" :label="t('Collabs')" icon="pi pi-share-alt" :explanation="{ what: t('Posts em parceria, nas duas direções.') }" />
        <StatsCard :value="countText(totals.views)" :label="t('Visualizações')" icon="pi pi-eye" :explanation="{ what: t('Soma das visualizações dos vídeos na última coleta.') }" />
      </div>

      <DataTable :config="tableConfig" :data="posts" :filterConfig="filterConfig" :loading="loading" :refreshButton="{ show: true }" @refresh="load" @button-click="onButton" />
    </template>
  </section>
</template>

<style scoped>
.instagram-analytics-page { padding: 24px; }
.instagram-analytics-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-bottom: 24px; }
</style>
